'use client';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 倒计时 Hook 配置选项
 */
interface UseCountdownOptions {
  countStart?: number; // 默认倒计时秒数
  intervalMs?: number; // 倒计时间隔时间 默认1秒,每秒执行一次
  onFinish?: () => void; // 倒计时结束回调
}

/**
 * 倒计时控制方法
 */
interface UseCountdownActions {
  startCountdown: (seconds?: number) => void;
  stopCountdown: () => void;
  resetCountdown: (seconds?: number) => void;
}

/**
 * 倒计时 Hook
 *
 * 特性：
 * - 基于时间戳计算，避免累积误差
 * - 开始倒计时时立即执行一次更新
 * - 自动清理定时器，防止内存泄漏
 * - 支持动态设置倒计时时间
 *
 * @param options 配置选项
 * @returns [当前倒计时秒数, 控制方法集合]
 *
 * @example
 * ```tsx
 * // 基础用法
 * const [count, { startCountdown, stopCountdown, resetCountdown }] = useCountdown({
 *   countStart: 60,
 *   onFinish: () => console.log('倒计时结束')
 * });
 *
 * // 开始倒计时（使用默认时间 60 秒）
 * startCountdown();
 *
 * // 开始倒计时（使用自定义时间 30 秒）
 * startCountdown(30);
 *
 * // 停止倒计时
 * stopCountdown();
 *
 * // 重置为默认时间（60 秒）
 * resetCountdown();
 *
 * // 重置为自定义时间（45 秒）
 * resetCountdown(45);
 * ```
 */
export const useCountdown = (options: UseCountdownOptions): [number, UseCountdownActions] => {
  const { countStart = 0, intervalMs = 1, onFinish } = options;
  const [count, setCount] = useState(0);
  const timer = useRef<NodeJS.Timeout | null>(null);
  const endTime = useRef<number>(0);
  const isRunning = useRef(false);

  const clearTimer = useCallback(() => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
    isRunning.current = false;
  }, []);

  const updateCountdown = useCallback(() => {
    const now = Date.now();
    const remainingTime = Math.ceil((endTime.current - now) / 1000);

    if (remainingTime <= 0) {
      clearTimer();
      setCount(0);
      onFinish?.();
      return 0;
    }

    setCount(remainingTime);
    return remainingTime;
  }, [clearTimer, onFinish]);

  const startCountdown = useCallback(
    (seconds?: number) => {
      if (isRunning.current) return;

      const startValue = seconds ?? countStart;
      const now = Date.now();
      endTime.current = now + startValue * 1000;
      isRunning.current = true;

      // 立即执行一次
      updateCountdown();

      // 设置定时器
      timer.current = setInterval(() => {
        updateCountdown();
      }, intervalMs * 1000);
    },
    [countStart, intervalMs, updateCountdown],
  );

  const stopCountdown = useCallback(() => {
    clearTimer();
    setCount(0);
    endTime.current = 0;
  }, [clearTimer]);

  const resetCountdown = useCallback(
    (seconds?: number) => {
      clearTimer();
      const resetValue = seconds ?? countStart;
      setCount(resetValue);
      endTime.current = 0;
    },
    [countStart, clearTimer],
  );

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  return [
    count,
    {
      startCountdown,
      stopCountdown,
      resetCountdown,
    },
  ];
};
